import { useState, useEffect, useCallback, useRef } from 'react';
import productCatalogueService from '../services/productCatalogueService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Custom hook for managing product catalogue data and operations
 * @param {Object} initialOptions - Initial configuration options
 * @returns {Object} Product catalogue state and methods
 */
export const useProductCatalogue = (initialOptions = {}) => {
  const {
    autoFetch = true,
    pageSize = 20,
    initialFilters = {}
  } = initialOptions;

  // Message alert for success notifications
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    category_id: '',
    // price filters removed — price moved to the SKU (migration 061)
    ...initialFilters
  });

  // Sort state
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });

  const currentPageRef = useRef(currentPage);
  const filtersRef = useRef(filters);
  const sortConfigRef = useRef(sortConfig);
  const pageSizeRef = useRef(pageSize);
  const lastFetchSignatureRef = useRef(null);
  const inFlightSignatureRef = useRef(null);
  // Deduplication helpers (similar to useTaxes pattern)
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  const inFlightSetRef = useRef(new Set());

  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    const now = Date.now();
    const elapsed = now - (lastFetchTimeRef.current || 0);
    return signature === lastSignatureRef.current && elapsed < 200;
  }, []);
  // Client-side cache for products to support local search without repeated API calls
  const cacheRef = useRef([]);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    sortConfigRef.current = sortConfig;
  }, [sortConfig]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  /**
   * Fetch products with current filters and pagination
   */
  const fetchProducts = useCallback(async (options = {}) => {
    const page = options.page ?? currentPageRef.current;

    const params = {
      page,
      limit: options.limit ?? pageSizeRef.current,
      search: filtersRef.current.search,
      category_id: filtersRef.current.category_id,
      sort_by: sortConfigRef.current.key,
      sort_order: sortConfigRef.current.direction,
      ...options.params
    };

    const signature = JSON.stringify(params);

    // Deduplicate: skip if identical request was recently made or currently in-flight
    if (!options.force) {
      if (inFlightSetRef.current.has(signature)) {
        return;
      }
      if (shouldDedupe(signature, options.force)) {
        return;
      }
    }

    // mark as in-flight
    inFlightSetRef.current.add(signature);
    setLoading(true);
    setError(null);

    try {
      const result = await productCatalogueService.getProductCatalogue(params);

      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        // update client-side cache when fetching from server
        cacheRef.current = list;
        setProducts(list);

        const totalRecords =
          result.total ?? result.pagination?.total ?? list.length;
        setTotalCount(totalRecords);

        const computedTotalPages =
          result.pagination?.total_pages ??
          Math.max(1, Math.ceil(totalRecords / (params.limit || pageSizeRef.current || 1)));
        setTotalPages(computedTotalPages);

        if (options.page && options.page !== currentPageRef.current) {
          setCurrentPage(options.page);
        }
      } else {
        setProducts([]);
        setError(result.error || i18n.t('productCatalogue.alerts.fetchFailed'));
      }
    } catch (err) {
      setProducts([]);
      setError(i18n.t('productCatalogue.alerts.unexpected'));
    } finally {
      // persist last signature/time for dedupe checks
      lastSignatureRef.current = signature;
      lastFetchTimeRef.current = Date.now();
      // clear in-flight marker
      inFlightSetRef.current.delete(signature);
      lastFetchSignatureRef.current = { signature, timestamp: Date.now() };
      inFlightSignatureRef.current = null;
      setLoading(false);
    }
  }, [shouldDedupe]);


  /**
   * Get product by ID
   */
  const getProductById = useCallback(async (productId, options = {}) => {
    const { withLoading = false } = options;

    if (withLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await productCatalogueService.getProductCatalogueById(productId);
      
      if (result.success) {
        return result.data;
      } else {
        setError(result.error);
        return null;
      }
    } catch (err) {
      setError(i18n.t('hookErrors.products.fetchOne'));
      return null;
    } finally {
      if (withLoading) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Create new product
   */
  const createProduct = useCallback(async (productData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await productCatalogueService.createProductCatalogue(productData);
      
      if (result.success) {
        // Update local state instead of refetching
        const newProduct = result.data;
        setProducts(prevProducts => {
          const updatedProducts = [newProduct, ...prevProducts];
          // Update cache as well
          cacheRef.current = updatedProducts;
          return updatedProducts;
        });

        // Update total count
        setTotalCount(prevCount => prevCount + 1);
        
        // Recalculate total pages
        setTotalPages(prevPages => Math.max(1, Math.ceil((totalCount + 1) / pageSizeRef.current)));
        
        // Show success message
        const productName = result.data?.product_name || i18n.t('productCatalogue.table.copy.unnamedProduct');
        showCreate(i18n.t('productCatalogue.notifications.created', { name: productName }));
        
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = i18n.t('productCatalogue.alerts.createFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showCreate, totalCount]);

  /**
   * Update product
   */
  const updateProduct = useCallback(async (productId, productData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await productCatalogueService.updateProductCatalogue(productId, productData);
      
      if (result.success) {
        // Update local state instead of refetching
        const updatedProduct = result.data;
        setProducts(prevProducts => {
          const updatedProducts = prevProducts.map(product => 
            product.id === productId ? { ...product, ...updatedProduct } : product
          );
          // Update cache as well
          cacheRef.current = updatedProducts;
          return updatedProducts;
        });
        
        // Show success message
        const productName = result.data?.product_name || i18n.t('productCatalogue.table.copy.unnamedProduct');
        showUpdate(i18n.t('productCatalogue.notifications.updated', { name: productName }));
        
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = i18n.t('productCatalogue.alerts.updateFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showUpdate]);

  /**
   * Delete product
   */
  const deleteProduct = useCallback(async (productId) => {
    setLoading(true);
    setError(null);

    try {
      // Get product details before deletion for the message
      const productToDelete = products.find(product => product.id === productId);

      const result = await productCatalogueService.deleteProductCatalogue(productId);

      // Prefer server returned name, then local cache, then fallback to id
      const productName = result?.data?.product_name || productToDelete?.product_name || String(productId);

      if (result.success) {
        // Update local state instead of refetching
        setProducts(prevProducts => {
          const updatedProducts = prevProducts.filter(product => product.id !== productId);
          // Update cache as well
          cacheRef.current = updatedProducts;
          return updatedProducts;
        });

        // Update total count
        setTotalCount(prevCount => Math.max(0, prevCount - 1));
        
        // Recalculate total pages
        setTotalPages(prevPages => Math.max(1, Math.ceil(Math.max(0, totalCount - 1) / pageSizeRef.current)));

        // Remove from selected if it was selected
        setSelectedProducts(prevSelected => prevSelected.filter(id => id !== productId));

        // Show success message using product name when available
        showDelete(i18n.t('productCatalogue.notifications.deleted', { name: productName }));

        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = i18n.t('productCatalogue.alerts.deleteFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [products, showDelete, totalCount]);

  /**
   * Bulk delete products
   */
  const bulkDeleteProducts = useCallback(async (productIds) => {
    if (!productIds || productIds.length === 0) {
      return { success: false, error: i18n.t('productCatalogue.alerts.noSelection') };
    }

    setLoading(true);
    setError(null);

    try {
      // Capture product names from local cache where possible (fallback to id)
      const productNames = productIds.map(id => {
        const p = products.find(prod => prod.id === id);
        return p?.product_name || String(id);
      });

      const result = await productCatalogueService.bulkDeleteProductCatalogue(productIds);

      if (result.success) {
        // Update local state instead of refetching
        setProducts(prevProducts => {
          const updatedProducts = prevProducts.filter(product => !productIds.includes(product.id));
          // Update cache as well
          cacheRef.current = updatedProducts;
          return updatedProducts;
        });

        // Update total count
        setTotalCount(prevCount => Math.max(0, prevCount - productIds.length));
        
        // Recalculate total pages
        setTotalPages(prevPages => Math.max(1, Math.ceil(Math.max(0, totalCount - productIds.length) / pageSizeRef.current)));

        // Clear selected products
        setSelectedProducts([]);

        // Show success message including full product names when possible
        if (productIds.length === 1) {
          const name = productNames[0] || String(productIds[0]);
          showDelete(i18n.t('productCatalogue.notifications.bulkDeletedSingle', { product: name }));
        } else {
          // Join all product names and show them in the alert (fallback to ids where name missing)
          const allNames = productNames.join(', ');
          // Try to use a localized template that accepts names, else fallback to joined names
          const template = i18n.t('productCatalogue.notifications.bulkDeletedByName');
          if (template && template.includes('{names}')) {
            showDelete(template.replace('{names}', allNames));
          } else {
            showDelete(allNames);
          }
        }

        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = i18n.t('productCatalogue.alerts.deleteFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showDelete, products, totalCount]);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchProducts({ page, force: true });
    }
  }, [currentPage, totalPages, fetchProducts]);

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback((newFilters) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setFilters(prevFilters => {
      const merged = { ...prevFilters, ...newFilters };
      filtersRef.current = merged;
      setCurrentPage(1);
      // Always server-fetch so totals and pagination are correct
      searchDebounceRef.current = setTimeout(() => {
        fetchProducts({ page: 1, force: true });
      }, 400);
      return merged;
    });
  }, [fetchProducts]);

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback((key, direction) => {
    setSortConfig({ key, direction });
    fetchProducts({ force: true });
  }, [fetchProducts]);

  /**
   * Handle selection change
   */
  const handleSelectionChange = useCallback((selected) => {
    setSelectedProducts(selected);
  }, []);

  /**
   * Selection methods
   */
  const selectProduct = useCallback((productId, isSelected) => {
    if (isSelected) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  }, []);

  const selectAllProducts = useCallback((shouldSelectAll = true) => {
    if (shouldSelectAll) {
      setSelectedProducts(products.map(product => product.id));
    } else {
      setSelectedProducts([]);
    }
  }, [products]);

  const clearSelection = useCallback(() => {
    setSelectedProducts([]);
  }, []);

  const isProductSelected = useCallback((productId) => {
    return selectedProducts.includes(productId);
  }, [selectedProducts]);

  const isAllSelected = useCallback(() => {
    return products.length > 0 && selectedProducts.length === products.length;
  }, [products, selectedProducts]);

  // Computed values
  const hasProducts = products.length > 0;
  const hasSelection = selectedProducts.length > 0;
  const selectionCount = selectedProducts.length;

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      category_id: ''
    });
    setCurrentPage(1);
    fetchProducts({ page: 1, force: true });
  }, [fetchProducts]);

  /**
   * Export products as Excel via the server-side download endpoint
   */
  const exportProducts = useCallback(async () => {
    const currentFilters = filtersRef.current;
    const params = {};
    if (currentFilters.search) params.search = currentFilters.search;
    if (currentFilters.category_id) params.category_id = currentFilters.category_id;

    const result = await productCatalogueService.exportProductCatalogue(params);
    if (!result.success) {
      return { success: false, error: result.error };
    }

    const disposition = result.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = match ? match[1].replace(/['"]/g, '') : 'product_catalogue.xlsx';

    const blobUrl = URL.createObjectURL(result.data);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);

    return { success: true };
  }, []);

  /**
   * Refresh data
   */
  const refresh = useCallback(() => {
    fetchProducts({ force: true });
  }, [fetchProducts]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchProducts();
    }
  }, [autoFetch, fetchProducts]);


  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    products,
    loading,
    error,
    clearError,
    selectedProducts,
    
    // Pagination
    currentPage,
    totalPages,
    totalCount,
    
    // Filters and sorting
    filters,
    sortConfig,
    
    // Methods
    fetchProducts,
    getProductById,
    createProduct,
    updateProduct,
    deleteProduct,
    bulkDeleteProducts,
    exportProducts,
    handlePageChange,
    handleFilterChange,
    handleSortChange,
    handleSelectionChange,
    selectProduct,
    selectAllProducts,
    clearSelection,
    isProductSelected,
    isAllSelected,
    clearFilters,
    refresh,
    
    // Computed values
    hasProducts,
    hasSelection,
    selectionCount,
    
    // State setters (for direct manipulation if needed)
    setProducts,
    setLoading,
    setError,
    setSelectedProducts,
    setFilters,
    setSortConfig
  };
};

export default useProductCatalogue;